<template>
	<v-card v-show="!isSubpageOpen" :class="cardClass()" class="pa-0">
		<v-card-text class="flex-grow-0 flex-shrink-0">
			<div class="header">
				<div>
					<h2><span class="action-header-upload-photo">Add</span> {{ dynamicActionText }}</h2>
				</div>
			</div>
			<v-form ref="form" v-model="form" :autocomplete="autocomplete">
				<CustomerAutoComplete
					v-model="selectedCustomer"
					label="Contact (Optional)"
					density="compact"
					@update:modelValue="setCustomer" />

				<ProjectAutoComplete
					:model-value="newSelectedProject"
					:disabled="disableProjectField"
					:entity="null"
					density="compact"
					label="Project (Optional)"
					@update:modelValue="setProject" />
				<AddressLookupLink v-if="!disableProjectField" @openAddressLookup="openAddressLookup" />

				<!-- LSP-20406: tag the session as part of starting the action. -->
				<SessionTagPicker v-model="selectedSessionTags" />
				<SmartPackSelector
					v-model="scribePackSelection"
					ref="smartPackSelector"
					@update:processing="scribePackProcessing = $event"
					@update:user-input-pending="scribeUserInputPending = $event" />
				<v-alert
					v-if="scribeUserInputPending"
					type="info"
					variant="tonal"
					density="compact"
					class="mt-3 cursor-pointer"
					@click="$refs.smartPackSelector?.reopenDialog()">
					<div class="text-body-2">This Spark report requires additional input. Click here to provide the required details.</div>
				</v-alert>
				<ScheduledSparkNotice v-if="scribePackSelection" :delay-seconds="contentNotificationDelay" />
				<!-- LSP-18505: `files` is deliberately kept empty so VFileUpload never renders its own
				     per-file list. The staged batch lives in `filesToUpload` and is surfaced as a count
				     below — a list of thumbnails and filenames is more than anyone needs here.

				     Bugbot: `disabled` is the SELECTION gate, not startButtonDisabled() — that one
				     includes `validating`, and a disabled VFileUpload ignores drops outright, so gating
				     it here undid the supersede design selectionBlockedReason() documents. Committing
				     mid-validation stays blocked (that is the confirm button); choosing different files
				     does not. -->
				<v-file-upload
					:model-value="files"
					multiple
					:accept="accept"
					icon="icon-cloud-stroke-upload"
					density="default"
					:disabled="!!selectionBlockedReason()"
					@update:modelValue="filesChanged">
					<template #browse="{ props }">
						<div class="d-flex flex-row ga-2 justify-space-between w-100">
							<!--Close-->
							<v-btn
								variant="outlined"
								rounded="xl"
								size="large"
								class="flex-1"
								@click.stop.prevent="close"
								@touchstart.stop.prevent="close()">
								Cancel
							</v-btn>

							<!--Start — LSP-18505: two states. With files staged this CONFIRMS the upload; without,
							    it is the picker activator (v-bind="props"). Selecting files no longer starts the
							    upload on its own, so a drag-and-drop can no longer outrun the Contact / Project /
							    Run this Spark fields above it. -->
							<PrimaryBtn
								v-if="hasStagedFiles"
								v-test="'upload-photo-confirm'"
								size="large"
								:readonly="startButtonDisabled()"
								:loading="starting"
								class="flex-1"
								@click.stop.prevent="startUpload">
								{{ dynamicActioningText }}
							</PrimaryBtn>
							<v-btn
								v-else
								v-bind="props"
								color="primary"
								variant="flat"
								rounded="xl"
								size="large"
								:readonly="startButtonDisabled()"
								:loading="validating"
								class="flex-1"
								@click.capture="prepareUploadStoragePersistence">
								{{ dynamicActioningText }}
							</v-btn>
						</div>
						<!-- LSP-18505: the whole of what a staged batch needs to say — how many are waiting,
						     and a way out of a wrong drop. Without a Clear the only escape would be Cancel,
						     which discards the Contact / Project / Spark selections too. -->
						<div
							v-if="hasStagedFiles && !starting"
							v-test="'upload-photo-staged-count'"
							class="d-flex flex-column align-center text-body-2 mt-3 w-100">
							<!-- One step above the row's text-body-2 so the count leads and the staged actions,
							     which keep 0.875rem via staged-action-btn, read as secondary to it. -->
							<span class="text-body-1 font-weight-bold">{{ stagedFilesSummary }}</span>
							<div class="d-flex flex-row flex-wrap justify-center align-center ga-2">
								<!-- LSP-19418: the way back INTO the picker. The primary button above stops being the
								     activator the moment a batch is staged, and it is the only activator VFileUpload has
								     here (its own input is invisible), so the staged page offered no way to add anything —
								     the reporter's whole complaint. It sits beside Clear rather than in the button row
								     because that row is already two large buttons wide at 427px, and because adding to a
								     batch is secondary to committing it. Same v-bind="props" the primary activator uses, so
								     it opens the same picker; `readonly` rather than `disabled` per the pattern in this
								     file (CLAUDE.md "Form action buttons — always enabled, no-op when invalid").

								     Break-it round 1: gated on selectionBlockedReason(), NOT startButtonDisabled().
								     The latter includes `validating`, and the docblock on selectionBlockedReason()
								     spells out why blocking a NEW selection on validation is wrong — the dropzone
								     deliberately stays open during it. Gating this button there would have left a
								     touch user with no way to add while a large video validated, while a desktop
								     user could still drop: the exact asymmetry this ticket exists to remove.

								     Break-it round 2, corrected by the server review: there is deliberately no
								     `:loading="validating"` here, but not because `.v-btn--loading` blocks pointer
								     events — `.v-btn--readonly` sets `pointer-events: none` too, so this button is
								     already click-gated on `selectionBlockedReason()`. The reason is that
								     `validating` is BROADER than `selectionBlockedReason()`: binding a spinner to it
								     would extend the block across the whole validation window, which is the one
								     window this button most needs to stay usable. The "Preparing your files…" row
								     below already reports validation, and the primary button keeps its spinner. -->
								<v-btn
									v-bind="props"
									v-test="'upload-photo-add-more'"
									variant="text"
									size="small"
									color="primary"
									:readonly="!!selectionBlockedReason()"
									class="staged-action-btn px-1 text-none font-weight-regular"
									@click.capture="prepareUploadStoragePersistence"
									>add more files</v-btn
								>
								<v-btn
									variant="text"
									size="small"
									color="primary"
									class="staged-action-btn px-1 text-none font-weight-regular"
									@click.stop.prevent="clearStagedFiles"
									>{{ stagedFilesClearLabel }}</v-btn
								>
							</div>
						</div>
						<!-- Reassure the user that work is happening while selected files (especially
						     large iPhone videos) are validated and prepared — LSP-16872. -->
						<div
							v-if="validating || starting"
							class="d-flex align-center justify-center ga-2 text-caption text-medium-emphasis mt-3 w-100">
							<v-progress-circular indeterminate size="16" width="2" color="primary" />
							<span>Preparing your files… large videos can take a moment.</span>
						</div>
					</template>
				</v-file-upload>
			</v-form>
		</v-card-text>
	</v-card>
	<AddressLookup v-if="isAddressLookupOpen" @projectSelected="setProject" @close="isAddressLookupOpen = false" />
</template>

<script>
	import api from "../api";
	import { resolveProjectPrefill } from "@/helpers/projectPrefill";
	import "../helpers/emoji";
	import constants from "../api/constants";
	import ProjectAutoComplete from "@/components/formfields/ProjectAutoComplete.vue";
	import SessionTagPicker from "@/components/tags/SessionTagPicker.vue";
	import timezones from "@/helpers/timezones";
	import { getDateTokens } from "@/helpers/dateFormat";
	import DeviceUtility from "@/helpers/device";
	import EventBus from "@/helpers/eventBus";
	import { closeActionDialog } from "@/helpers/closeActionDialogs";
	import CustomerAutoComplete from "@/components/formfields/CustomerAutoComplete.vue";
	import { calculateTimeout, getMessageFromError, uploadSupportEmail } from "../helpers/utilities";
	import { validateUploadFile, UNREADABLE_FILE_MESSAGE } from "../helpers/fileValidation";
	import { buildAcceptAttribute, correctDeclaredMimeType, getFilesByType, mergeStagedFiles } from "@/helpers/fileUploads";
	import { processWithConcurrency } from "@/services/chunkedUploadService";
	import { buildFileTooLargeForUploadMessage, exceedsMultipartPartLimit, formatFileSize } from "@uploads";
	import { logInfo, logError } from "@/helpers/logError";
	import { detectPlatform, getLargeFileWarningBytes, findLargeFiles } from "@common-ui/utilities/browserDetection";
	import AddressLookup from "./location/AddressLookup.vue";
	import AddressLookupLink from "./location/AddressLookupLink.vue";
	import SmartPackSelector from "./scribe/SmartPackSelector.vue";
	import ScheduledSparkNotice from "./scribe/ScheduledSparkNotice.vue";
	import { getVueTelProps } from "@/helpers/phone";
	import { useToast } from "vue-toastification";

	// Cap on simultaneous file validations. Each validateUploadFile() reads ~64KB of a file; for
	// cloud-backed iPhone media that read can pull the file from iCloud, so a large multi-select
	// must not fire every read at once (thundering herd). Mirrors the initiate fan-out cap (LSP-16872).
	const FILE_VALIDATION_CONCURRENCY = 5;

	export default {
		components: {
			ProjectAutoComplete,
			SessionTagPicker,
			CustomerAutoComplete,
			AddressLookup,
			AddressLookupLink,
			SmartPackSelector,
			ScheduledSparkNotice
		},
		setup() {
			const toast = useToast();
			return { toast };
		},
		mixins: [closeActionDialog],
		props: {
			conversationType: String,
			customerId: String,
			actionText: String,
			actioningText: String,
			allowJoin: Boolean,
			buttonIcon: String,
			showHeader: Boolean,
			projectId: String,
			project: {
				type: Object,
				required: false
			},
			allowDocs: {
				type: Boolean,
				default: true
			},
			projectChecklistItemId: {
				type: String,
				default: ""
			}
		},
		// "uploadCounts" is declared because the parent (ActionDialog) attaches an
		// @uploadCounts listener; once the root is a v-show fragment (LSP-15455) an
		// undeclared listener triggers a Vue fragment-fallthrough warning. Upload counts
		// are currently published via EventBus, so this declaration is for the listener
		// contract, not a local this.$emit.
		emits: ["close-menu", "uploadCounts"],
		data() {
			return {
				isAddressLookupOpen: false,
				showPhoneFieldErrorState: false,
				autocomplete: Math.random().toString(),
				form: false,
				files: [],
				currentTab: null,
				vueTelProps: getVueTelProps(),
				starting: false,
				validPhone: false,
				autoSetName: false,
				autoSetNameValue: "",
				emailDisabled: true,
				email: "",
				phone: "",
				phoneSearch: "",
				title: "",
				name: "",
				contactName: "",
				includeEmailInvitation: false,
				meetingUrl: "",
				actioningTextOverride: "",
				organization: {},
				autoStartRecordings: true,
				notificationTemplate: "",
				defaultNotificationTemplate: "",
				shareUser: null,
				me: null,
				selectedCustomer: null,
				validCustomer: true,
				phoneSearchResultsOpen: false,
				customerJustSelected: false,
				newSelectedProject: null,
				// LSP-20406: tags applied to the session at creation.
				selectedSessionTags: [],
				projectSelectItems: [],
				isCameraOpen: false,
				capturedImage: null,
				cameraWindow: null,
				stream: null,
				filesToUpload: [],
				// LSP-18505: validation of a freshly selected batch is now its own phase, separate from
				// `starting` (which covers the actual upload). Splitting them lets the button show
				// "Preparing your files…" during validation and "Upload N files" once staged.
				validating: false,
				// break-it round 1 (LSP-18505): validation is async, so staging happens AFTER an await. A
				// second selection, a Clear, or a Cancel during that window used to let the stale batch
				// write itself into filesToUpload when it finally finished — resurrecting files the user
				// had just replaced or dismissed. That was solved with a `selectionSequence` token: only
				// the newest selection could stage.
				//
				// LSP-19418 replaced the token with these two. Staging ADDS now, so two overlapping
				// selections are not rivals — the user picked both batches and wants both, and "newest
				// wins" would silently drop one. What is left of the original concern splits cleanly:
				//
				//  - `unstageEpoch` — bumped ONLY by clearStagedFiles() and reset() (Cancel, and save()'s
				//    success path). An explicit un-stage still invalidates every validation in flight,
				//    so a batch that finishes reading afterwards cannot resurrect itself.
				//  - `validationsInFlight` — how many selections are still being read. `validating`
				//    drives the "Preparing your files…" row AND startButtonDisabled(), so it has to mean
				//    "SOME selection is still being read". Break-it round 2 proved the alternative loses
				//    a file: with the flag owned by the newest run, a fast second pick cleared it while a
				//    large video was still validating, the commit gate opened, save() snapshotted the
				//    batch without the video, and reset() then dropped it.
				unstageEpoch: 0,
				validationsInFlight: 0,
				// Monotonic id per selection, for log correlation only — overlapping selections are the
				// design now, so a breadcrumb without one cannot be attributed to a run.
				selectionRunCount: 0,
				cachedUser: {},
				placeholderTitle: "",
				disableProjectField: false,
				scribePackSelection: null,
				scribePackProcessing: false,
				scribeUserInputPending: false
			};
		},
		computed: {
			contentNotificationDelay() {
				return this.me?.config?.contentNotificationDelaySeconds;
			},
			isSubpageOpen() {
				return this.isAddressLookupOpen;
			},
			accept() {
				return buildAcceptAttribute({ allowDocs: this.effectiveAllowDocs });
			},
			/**
			 * LSP-17928: temporary guard — checklist items don't render document attachments yet.
			 * Keyed off `projectChecklistItemId` directly (the true "this upload targets a checklist
			 * item" signal), NOT `allowDocs` — `allowDocs` only happens to be false for checklist
			 * uploads today because ProjectChecklists.vue is the one caller that sets it; nothing
			 * enforces that pairing. Remove this computed once checklist items support documents.
			 */
			isChecklistUpload() {
				return !!this.projectChecklistItemId;
			},
			/**
			 * LSP-17928: temporary guard — true only when documents are BOTH allowed by the caller AND
			 * this isn't a checklist-item upload. Drives `accept()` + `dynamicActionText()`, so
			 * the OS picker and header copy stay consistent with the real enforcement point in
			 * `filesChanged()` (previously `accept`/`dynamicActionText` read `allowDocs` directly, so a
			 * caller that set `projectChecklistItemId` without also setting `allowDocs=false` would
			 * still advertise/offer PDFs before rejecting them after the fact — review-round-2 finding).
			 * Remove once checklist items support documents.
			 */
			effectiveAllowDocs() {
				return this.allowDocs && !this.isChecklistUpload;
			},
			/**
			 * LSP-17928: temporary guard — excludes "pdf" from the categories `validateUploadFile`
			 * accepts for a checklist-item upload. The OS file-picker `accept` attribute above already
			 * hints this, but it's bypassable (drag-and-drop, an OS picker that ignores `accept`), so
			 * this is the real guard. Keyed off `effectiveAllowDocs` (not `isChecklistUpload` alone) so
			 * this stays consistent with `accept()`/`dynamicActionText()` for a non-checklist caller that
			 * also sets `allowDocs=false` — review round found this could otherwise silently allow "pdf"
			 * through validation despite the picker/header already saying documents aren't offered.
			 * Remove this computed + its call site in `filesChanged()` once checklist items support
			 * documents.
			 */
			allowedUploadCategories() {
				return this.effectiveAllowDocs ? ["image", "video", "pdf"] : ["image", "video"];
			},
			/**
			 * LSP-18505: files have been selected and validated but NOT uploaded yet. While this is
			 * true the dialog stays put with Contact / Project / Run this Spark still editable.
			 */
			hasStagedFiles() {
				return this.filesToUpload.length > 0;
			},
			/** LSP-18505: the count is the whole status — no filenames, no thumbnails. */
			stagedFilesSummary() {
				const count = this.filesToUpload.length;
				return `${count} ${count === 1 ? "file" : "files"} selected`;
			},
			/**
			 * LSP-18505: the clear action names what it will drop rather than saying a bare "Clear", so
			 * it cannot be misread as clearing the Contact / Project / Spark selections above it.
			 */
			stagedFilesClearLabel() {
				const count = this.filesToUpload.length;
				return count === 1 ? "clear this file" : `clear these ${count} files`;
			},
			dynamicActioningText() {
				if (this.actioningTextOverride) {
					return this.actioningTextOverride;
				}
				if (this.hasStagedFiles) {
					const count = this.filesToUpload.length;
					return `Upload ${count} ${count === 1 ? "file" : "files"}`;
				}
				return "Select Files";
			},
			dynamicActionText() {
				if (this.effectiveAllowDocs) {
					return "Photos, Documents, or Videos";
				} else return "Photos or Videos";
			},
			computedTitle() {
				if (this.me) {
					return `Files from ${this.getTeamMemberName()} on ${timezones.formatDate(new Date().toISOString(), getDateTokens(this.organization?.dateFormat).dateShortMonth)}`;
				} else {
					return `Files on ${timezones.formatDate(new Date().toISOString(), getDateTokens(this.organization?.dateFormat).dateShortMonth)}`;
				}
			}
		},
		async mounted() {
			// order matters here
			this.reset();
			this.tryOpenAddressLookup();
			this.me = await api.getMe();
			// Must precede reading computedTitle: it formats the name date via
			// this.organization.dateFormat (LSP-15050).
			this.organization = this.me.organization;

			this.title = this.computedTitle;
			this.placeholderTitle = this.title;

			if (this.project) {
				this.newSelectedProject = this.project;
			} else if (this.projectId) {
				const result = await api.getProject(this.projectId);
				this.newSelectedProject = result;
			}

			if (this.projectChecklistItemId || this.projectId || this.project) {
				this.disableProjectField = true;
			}

			// LSP-16205: when opened from a project, default the contact to the project's
			// first assigned contact so the user isn't forced to re-search. An explicit
			// customerId prop still takes precedence, and the prefilled value stays editable.
			const { project: resolvedProject, customerId: prefillCustomerId } = await resolveProjectPrefill({
				customerId: this.customerId,
				projectId: this.projectId,
				project: this.newSelectedProject ?? this.project
			});
			if (resolvedProject) {
				this.newSelectedProject = resolvedProject;
			}
			if (prefillCustomerId) {
				try {
					const result = await api.getCustomers({ where: { id: prefillCustomerId } });
					if (result.count > 0) {
						this.selectedCustomer = result.rows[0];
					}
				} catch (error) {
					// A failed prefill is non-critical — degrade to "no contact prefilled" rather than
					// aborting the rest of mounted() (organization/setup below run on this same path).
					console.warn(`[LSP-16205] contact prefill lookup failed for customer ${prefillCustomerId}`, error);
				}
			}
		},
		methods: {
			prepareUploadStoragePersistence() {
				void this.$root.$fileUploadDialog?.requestUploadStoragePersistence?.();
			},
			tryOpenAddressLookup() {
				const params = new URLSearchParams(window.location.search);
				const goTo = params.get("goTo");
				if (goTo === "address") {
					this.openAddressLookup();
				}
			},
			openAddressLookup() {
				this.isAddressLookupOpen = true;
			},
			// TODO: Consolidate this into a helper or single Action dialog component
			getTeamMemberName() {
				let name = null;

				if (this.me?.user.firstname) {
					name = this.me?.user.firstname;
				}

				if (this.me?.user.lastname) {
					name = `${name} ${this.me?.user.lastname}`;
				}

				if (!name) {
					name = this.me?.user.email;
				}

				return name ?? "-";
			},
			async setProject(project) {
				this.isAddressLookupOpen = false;
				this.newSelectedProject = project;
			},
			cardClass() {
				return `pa-1 conversation-card ${DeviceUtility.isMobile ? "conversation-bottom" : ""} ${this.conversationType}`;
			},
			startButtonDisabled() {
				return this.starting || this.validating || !!this.selectionBlockedReason();
			},
			/**
			 * break-it round 1: what genuinely blocks a NEW selection — deliberately excluding
			 * `validating`. Gating selection on `validating` meant a second drop while the first batch
			 * was still being read got thrown away silently, which is worse than the bug this ticket
			 * fixes. LSP-19418: a newer selection no longer supersedes an older one at all — staging
			 * adds, so both land (see `unstageEpoch` in data()).
			 */
			selectionBlockedReason() {
				if (this.starting) return "upload-in-flight";
				if (this.actioningTextOverride) return "actioning-text-override";
				if (this.scribePackProcessing) return "spark-pack-processing";
				return "";
			},
			reset() {
				this.$refs.smartPackSelector?.reset?.();
				this.isAddressLookupOpen = false;
				this.phone = "";
				this.email = "";
				this.includeEmailInvitation = false;
				this.name = "";
				this.autoSetName = "";
				this.autoSetNameValue = "";
				this.shareUser = null;
				this.selectedCustomer = null;
				this.selectedSessionTags = [];
				this.files = [];
				// develop's break-it round 6 found this independently: the picker list was cleared but the
				// validated batch was not, so the next upload from the same form re-sent the previous
				// files. LSP-18505 needs the same clear for its own reason — staged-but-not-uploaded
				// files are dialog state now, and a reopened dialog would otherwise offer
				// "Upload N files" for a batch the user never re-selected.
				this.filesToUpload = [];
				// LSP-18505 break-it round 1, additionally: bump the selection token and drop the
				// validating flag, or a validation still running when the user backs out restages its
				// batch into a dialog they have already left.
				// LSP-19418: Cancel and the post-upload reset un-stage, so they invalidate every validation
				// still in flight, and nothing those runs would have reported is wanted any more.
				this.unstageEpoch++;
				this.validationsInFlight = 0;
				this.validating = false;
				this.scribePackSelection = null;
				this.scribePackProcessing = false;
				this.scribeUserInputPending = false;
			},
			/**
			 * LSP-18505 — validates and STAGES the selected files. It does not upload.
			 *
			 * This fires for both ways of choosing files: Vuetify's VFileUpload emits
			 * `update:modelValue` for a drag-and-drop onto the dropzone and for a pick through the
			 * "Select Files" activator, so there has only ever been one code path here. It used to end
			 * in `save()`, which meant the act of choosing a file WAS the act of uploading it — and a
			 * drag-and-drop is a single motion straight from the desktop into the dialog, so it fired
			 * before the user could touch the Contact / Project / Run this Spark fields sitting above
			 * the dropzone. Those fields are read at `save()` time, so they weren't ignored so much as
			 * never given a chance.
			 *
			 * Staging splits the two: choosing files fills the list, and `startUpload()` (the primary
			 * button, now reading "Upload N files") is what commits. The upload-time guards moved with
			 * it — blocking selection on a pending Spark input, for instance, is backwards now that a
			 * user can legitimately stage files first and fill the Spark form second.
			 *
			 * @param {File[]} files - The files the user just selected, dropped, or cleared
			 */
			async filesChanged(files) {
				// LSP-19418: read (do not bump) the un-stage epoch. This run is invalidated only if the
				// user clears the batch out from under it, not by a later selection — see the gate in
				// stageSelection().
				const unstageToken = this.unstageEpoch;
				// Overlapping selections are the design now, so every run carries an id: the breadcrumbs
				// in stageSelection are otherwise impossible to attribute to a run when two are in
				// flight (server review).
				const runId = ++this.selectionRunCount;
				this.validationsInFlight++;
				try {
					await this.stageSelection(files, unstageToken, runId);
				} finally {
					// The ONE place this run gives its count back. It used to be four explicit calls, one
					// per `return` — which balanced every normal exit and none of the exceptional ones. A
					// single throw anywhere in stageSelection (a toast on an unmounting component, the
					// File constructor inside the MIME correction, the partitioner) leaked the increment,
					// and a leaked increment wedges `validating` true forever: startButtonDisabled() then
					// holds the confirm button AND the picker activator down for the rest of the dialog's
					// life, with no toast and no error, recoverable only by Cancel or Clear — both of which
					// discard the batch and the Contact / Project / Spark fields with it. This app has no
					// app.config.errorHandler net and the template handler has no .catch, so nothing else
					// would have caught it either. settleValidation is epoch-guarded, so running it on
					// every path is safe (server review, critical finding 1).
					this.settleValidation(unstageToken);
				}
			},
			/**
			 * LSP-19418 — the body of filesChanged. Split out so the in-flight count is balanced by one
			 * `finally` in the caller instead of a settle call at every `return`; the comment there says
			 * what that was leaking.
			 *
			 * @param {File[]} files - The files the user just selected, dropped, or cleared
			 * @param {number} unstageToken - the un-stage epoch this run was counted in
			 * @param {number} runId - correlates this run's breadcrumbs when selections overlap
			 */
			async stageSelection(files, unstageToken, runId) {
				if (!files || files.length === 0) {
					// A selection that yielded no files. This used to drop the staged batch, on the
					// reasoning that it also covered "the user cleared the staged list via VFileUpload's own
					// per-item remove" — but `files` is deliberately kept empty (see the template), so
					// VFileUpload renders no per-item list and that remove button does not exist.
					//
					// LSP-19418: nothing that reaches here is a request to un-stage. A cancelled OS picker
					// does not reach it at all (a cancel fires `cancel`, not `change`, and VFileUpload's own
					// onFileSelection returns early on an empty FileList), so what is left is a DROP that
					// yielded no files — dragged text, an empty folder, or a drop the accept filter rejected
					// outright. Destroying a staged batch because a stray drag landed on the dropzone is
					// exactly the silent loss this ticket is about. `clearStagedFiles` is the only un-stage.
					//
					// Bugbot (MEDIUM): this path returned WITHOUT clearing `validating`, and the run it
					// superseded left the flag alone too, so nobody cleared it: the dropzone stayed
					// disabled behind a permanent "Preparing your files…" until Cancel, which also discards
					// Contact / Project / Spark. The caller's `finally` is what owns that now.
					//
					// CLAUDE.md "Diagnostic logging when fixing silent failures": this returns without
					// acting, and this diff changes what it does, so it says why.
					logInfo("[Upload] ActionUploadPhoto: selection produced no files, keeping the staged batch", {
						runId,
						stagedCount: this.filesToUpload.length
					});
					this.files = [];
					return;
				}
				if (this.selectionBlockedReason()) {
					// CLAUDE.md "Diagnostic logging when fixing silent failures": the selection is dropped
					// with no user-visible feedback at all, so name the condition that swallowed it.
					logInfo("[Upload] ActionUploadPhoto: selection refused, dropping the picked files", {
						runId,
						reason: this.selectionBlockedReason(),
						fileCount: files.length,
						stagedCount: this.filesToUpload.length
					});
					this.resetFilePicker();
					return;
				}
				this.validating = true;
				// break-it round 1: accumulate LOCALLY. This used to clear and push straight into
				// `this.filesToUpload`, so two overlapping selections interleaved their writes into the
				// same array and whichever finished last won — regardless of which the user actually
				// chose. The batch is only published to the component under the un-stage gate below.
				const validatedFiles = [];

				// Validate selected files concurrently (was one-at-a-time, which stacked the per-file
				// ~64KB magic-byte read — slow for cloud-backed iPhone videos — and delayed the upload
				// dialog, LSP-16872). Bounded to FILE_VALIDATION_CONCURRENCY so a large multi-select does
				// not fire every cloud read at once. Each result is written to its input index so
				// filesToUpload preserves the order the user picked.
				const validationStartedAt = performance.now();
				const videoCount = files.filter((file) => file?.type?.startsWith("video/")).length;
				logInfo("[Upload] ActionUploadPhoto: validating selected files", { runId, fileCount: files.length, videoCount });

				const validations = new Array(files.length);
				await processWithConcurrency(files, FILE_VALIDATION_CONCURRENCY, async (file, index) => {
					if (!file) {
						validations[index] = { file, status: "skip" };
						return;
					}
					try {
						// LSP-17928 break-it round 4 (Bugbot, 2nd pass): validateUploadFile does the SAME
						// magic-byte read as isValidUploadFile but also surfaces what it detected — captured
						// once here so the checklist correction below never needs a second, independently
						// fallible read (a re-read that times out/throws where the first one succeeded would
						// silently skip the correction and let a mismatched file through unfixed).
						const { valid, detectedMimeType, unreadable } = await validateUploadFile(file, this.allowedUploadCategories);
						validations[index] = { file, status: unreadable ? "unreadable" : valid ? "valid" : "invalid", detectedMimeType };
					} catch (error) {
						// Read failed (e.g. a stale iCloud-backed handle — the LSP-16872 scenario). Record the
						// skip FIRST so a hostile rejection value can never turn the diagnostic into a second
						// failure that breaks the batch (leaving this index unset would choke the consume loop).
						validations[index] = { file, status: "unreadable" };
						// The user gets a per-file toast below; log a Datadog breadcrumb too so a future "why was
						// this file skipped" triage has the file name and reason. Stringify defensively — a
						// rejected value's `.message`/`toString` can itself throw.
						let reason;
						try {
							reason = error instanceof Error ? error.message : String(error);
						} catch {
							reason = "unstringifiable error";
						}
						logInfo("[Upload] ActionUploadPhoto: file unreadable, skipping", { fileName: file.name, reason });
					}
				});

				// Bugbot: gate EVERYTHING below on one check, not just the publish. Every toast and
				// checklist-rejection log from here on belongs to THIS batch, and a discarded run was
				// still emitting them — so a batch the user had already dismissed narrated its own
				// rejections over the current one. The tail from here is synchronous, so one check
				// makes it atomic.
				//
				// LSP-19418 break-it round 1: this gate used to be "a newer selection exists" (a monotonic
				// `selectionSequence` token, now gone),
				// so this run was dropped whenever another followed it. That was right while a selection
				// REPLACED the batch. Now that it adds, dropping the loser is silent data loss: drop a
				// large video, drop a second file while the first is still validating, and the first
				// file vanishes with no toast — the user dropped two and got one. Both selections are
				// additive, so both must land. What still invalidates this run is the user un-staging
				// underneath it (Clear, Cancel, or the reset() after a finished upload).
				if (unstageToken !== this.unstageEpoch) {
					// Silent-failure rule: this returns without acting, so say why once.
					logInfo("[Upload] ActionUploadPhoto: staged batch was cleared during validation, discarding batch", {
						runId,
						fileCount: files.length,
						elapsedMs: Math.round(performance.now() - validationStartedAt)
					});
					return;
				}

				for (const { file, status, detectedMimeType } of validations) {
					if (status === "skip") continue;

					if (status === "unreadable") {
						// Review round: this branch was missing the checklist diagnostic the sibling
						// "invalid" branch below already has, breaking the cross-platform breadcrumb trail
						// for a checklist upload that fails the magic-byte read itself (not just a
						// recognized-but-disallowed type).
						this.logChecklistRejectionIfApplicable(file.name);
						const message = `"${file.name}": ${UNREADABLE_FILE_MESSAGE}`;
						this.toast.error(message, { timeout: calculateTimeout(message) });
						continue;
					}

					if (status === "invalid") {
						this.logChecklistRejectionIfApplicable(file.name);
						const message = this.invalidFileMessage(file.name);
						this.toast.error(message, { timeout: calculateTimeout(message) });
						continue;
					}

					// LSP-17928 break-it round 4 (Bugbot): a file that passed the magic-byte gate above
					// because its REAL content is an image/video can still carry a declared file.type/
					// extension that reads as a document. Nothing downstream of this component re-checks
					// magic bytes — FileUploadDialog's own getFilesByType/inferFileCategory calls trust the
					// declared type — so without correcting it here, the file would survive this guard only
					// to be uploaded as a document and vanish from the checklist UI, the exact failure mode
					// this whole guard exists to prevent (just reached by misrouting instead of outright
					// rejection). Reuses THIS validation pass's own detectedMimeType (no second read) and
					// LSP-19132: this correction used to apply ONLY to a checklist upload, on the reasoning
					// that "the mismatch is harmless for a normal upload (all three buckets are already
					// accepted there, so which one a file lands in doesn't gate anything)". That was true of
					// LSP-17928's concern — bucket misrouting — but not of this one. The declared type also
					// decides the S3 KEY EXTENSION (adminUploadTargetInitiator derives it from file.type), and
					// the snapshot Lambda branches on that extension: a JPEG named .heic mints a `.heic` key,
					// the Lambda runs heif-convert on JPEG bytes, throws, and never sends the finalize
					// message, so the Snapshot row stays pending forever and the photo silently never appears.
					// The mismatch is therefore not harmless on the ordinary path either, and the correction
					// now applies to every upload. Found by LSP-19132's architecture-gap review as a sibling
					// entry point reaching the same mint as useFileUploadFlow.
					validatedFiles.push(this.correctedFileForUpload(file, detectedMimeType));
				}

				// Category screen mirroring useFileUploadFlow: a file can pass magic-byte validation yet be
				// uncategorizable by the partitioner (empty file.type AND no usable extension). Without this
				// screen it would create a conversation in save() and then be silently dropped at
				// FileUploadDialog's getFilesByType — an orphan conversation + lost file (LSP-16872 review).
				//
				// LSP-17928 break-it round 1: documents are already excluded for a checklist-item upload by
				// validateUploadFile's magic-byte check above (allowedUploadCategories omits "pdf"), so this
				// pass must NOT re-exclude the documents bucket — getFilesByType classifies by declared
				// file.type/extension (inferMimeType), a weaker signal than magic bytes, and disagreeing with
				// the magic-byte verdict here would silently drop a real photo/video whose OS-reported MIME
				// happened to read "application/pdf" (a false rejection, not a security concern — the magic-byte
				// gate already proved the content is real media). Always include all three buckets; this pass's
				// only remaining job is dropping files that land in NONE of them (genuinely uncategorizable).
				const { images, documents, videos } = getFilesByType(validatedFiles);
				const bucketableFiles = new Set([...images, ...documents, ...videos]);
				const stageableFiles = validatedFiles.filter((file) => {
					if (!bucketableFiles.has(file)) {
						this.logChecklistRejectionIfApplicable(file.name);
						const message = this.invalidFileMessage(file.name);
						this.toast.error(message, { timeout: calculateTimeout(message) });
						return false;
					}
					// LSP-19397: upload-size screen, in the same pass and for the same reason as the category
					// screen above — save() creates the conversation, so a file that can never upload must be
					// dropped BEFORE it, or the user gets an orphan conversation plus the API's raw wire error
					// (`totalChunks must be an integer between 1 and 1000.`), which is what the reporting
					// customer saw three times in a row. Shares `exceedsMultipartPartLimit` with
					// initiateUploadTargets, so this screen and the engine cannot disagree about the boundary.
					if (exceedsMultipartPartLimit(file.size)) {
						// Deliberately NOT logChecklistRejectionIfApplicable: that helper hard-codes
						// "rejected — invalid file" and the cross-platform `upload.checklist_document_rejected`
						// event that mobile also emits, so routing a SIZE refusal through it mislabels the
						// reason and pollutes the stream "my checklist upload vanished" triage reads (server
						// review, PR #4659). The checklist correlation is carried on this accurate log instead.
						logInfo("[Upload] ActionUploadPhoto: file exceeds the multipart upload ceiling, dropping", {
							fileName: file.name,
							fileSize: formatFileSize(file.size),
							...(this.isChecklistUpload ? { projectChecklistItemId: this.projectChecklistItemId } : {})
						});
						// WARNING, not error — see the note in useFileUploadFlow's sibling screen.
						const message = buildFileTooLargeForUploadMessage(file.name, file.size, uploadSupportEmail());
						this.toast.warning(message, { timeout: calculateTimeout(message) });
						return false;
					}
					return true;
				});

				logInfo("[Upload] ActionUploadPhoto: validation complete", {
					runId,
					fileCount: files.length,
					videoCount,
					validCount: stageableFiles.length,
					elapsedMs: Math.round(performance.now() - validationStartedAt)
				});

				// LSP-18505: stage rather than upload. `files` stays empty on purpose — it is only
				// VFileUpload's model, and feeding it the batch is what made the component render its
				// per-file list. The count in the browse slot is the whole status the user needs.
				//
				// LSP-19418: ADD to the staged batch instead of replacing it. Replacing is what made
				// "select more media to add" impossible even on a desktop, where the dropzone stays live —
				// two staged files plus one dropped file rendered "1 file selected". It also means a
				// follow-up selection that is entirely rejected (`stageableFiles` empty) leaves the
				// existing batch alone: the rejection toasts above are that selection's whole outcome, and
				// what was already staged is not the new selection's to discard.
				const stagedCountBeforeMerge = this.filesToUpload.length;
				const { files: mergedFiles, skipped } = mergeStagedFiles(this.filesToUpload, stageableFiles);
				this.filesToUpload = mergedFiles;
				// Server review: every other rejection in this flow toasts (unreadable, invalid,
				// oversized) but a duplicate skip did not, so a user whose whole re-pick was already
				// staged watched the count not move with no signal at all. Toasted ONLY when nothing new
				// arrived — re-picking the staged files alongside a new one is the normal way to use
				// "add more files", so toasting the partial case would fire on the happy path.
				if (skipped.length > 0 && this.filesToUpload.length === stagedCountBeforeMerge) {
					const message =
						skipped.length === 1
							? `"${skipped[0].name}" is already in this upload.`
							: `Those ${skipped.length} files are already in this upload.`;
					this.toast.info(message, { timeout: calculateTimeout(message) });
				}
				// CLAUDE.md "Diagnostic logging when fixing silent failures": mergeStagedFiles drops a
				// re-picked file without a toast (by design — it is already staged), so "I picked it and
				// the count never moved" needs a breadcrumb naming the files that were actually skipped.
				if (skipped.length > 0) {
					logInfo("[Upload] ActionUploadPhoto: skipped files already staged", {
						runId,
						duplicateCount: skipped.length,
						fileNames: skipped.map((file) => file.name),
						stagedCount: this.filesToUpload.length
					});
				}
				this.resetFilePicker();
			},
			/** LSP-18505: drop a staged batch without closing the card (Cancel would discard the fields too). */
			clearStagedFiles() {
				// break-it round 1 (LSP-18505): supersede any validation still running, or it would
				// restage its batch on top of the clear the user just asked for. LSP-19418: the un-stage
				// epoch is what does that now — a later selection no longer discards an in-flight batch,
				// only an explicit un-stage does.
				this.unstageEpoch++;
				this.validationsInFlight = 0;
				this.validating = false;
				this.filesToUpload = [];
				this.resetFilePicker();
			},
			/**
			 * LSP-18505 — commits the staged batch. This is the only path that uploads.
			 *
			 * Holds the guards that used to sit in `filesChanged()`: they are upload-time conditions, and
			 * running them at selection time is what made choosing a file feel like a commitment.
			 */
			async startUpload() {
				if (this.starting || this.validating || !this.hasStagedFiles) {
					return;
				}
				// Bugbot (MEDIUM): `scribePackProcessing` used to be enforced here by accident — the old
				// single-step flow ran everything through filesChanged, which gated on
				// startButtonDisabled(). Splitting selection from commit dropped it, and the confirm
				// control is `readonly` rather than `disabled`, so a click while Spark details were still
				// loading uploaded with no Spark attached. Sibling action dialogs refuse to start in that
				// state; so does this one now.
				if (this.selectionBlockedReason()) {
					return;
				}
				if (!this.form || !this.title || this.title.trim() === "") {
					await this.$refs.form.validate();
					return;
				}
				if (this.projectId && !this.newSelectedProject) {
					this.toast.warning("Please select a project before uploading.");
					return;
				}
				if (this.scribeUserInputPending) {
					console.warn("Upload paused because the selected Spark still requires input.", {
						packId: this.scribePackSelection?.pack?.id,
						projectId: this.projectId
					});
					this.$refs.smartPackSelector?.reopenDialog?.();
					return;
				}

				this.starting = true;
				// The staged list stays on screen through the upload and is cleared by save()'s reset() on
				// success. On failure it survives deliberately, so a retry does not re-pick every file.
				this.save()
					.catch((error) => {
						// save() surfaces its own user-facing errors; this is a defensive net so an
						// unexpected rejection can never leave the button/spinner stuck in the loading
						// state (LSP-16872). Logged to Datadog rather than swallowed silently.
						logError("ActionUploadPhoto.save rejected unexpectedly", error);
					})
					.finally(() => {
						this.starting = false;
					});
			},
			/**
			 * LSP-17928: temporary guard — the "not a valid image, video, or PDF file" message only
			 * makes sense when PDFs are actually allowed. For a checklist-item upload a rejected file
			 * is more likely a document, so word it accordingly. Remove this method and inline the
			 * single remaining message once checklist items support documents.
			 */
			invalidFileMessage(fileName) {
				return this.isChecklistUpload
					? `"${fileName}" isn't a photo or video. Documents aren't supported on checklist items.`
					: `"${fileName}" is not a valid image, video, or PDF file and has been skipped.`;
			},
			/**
			 * LSP-17928: temporary guard — mirrors mobile's structured `upload.checklist_document_rejected`
			 * breadcrumb (UploadMediaViewModel.rejectsDocumentsForChecklistItem), so a "my checklist
			 * upload vanished" triage has the same diagnostic trail on both platforms. No-ops outside
			 * checklist context — the toast alone is sufficient there. Remove once checklist items
			 * support documents.
			 */
			logChecklistRejectionIfApplicable(fileName) {
				if (!this.isChecklistUpload) return;
				// Review round: added a structured "event" key matching mobile's
				// upload.checklist_document_rejected breadcrumb, so the two platforms' rejections are
				// queryable together in Datadog despite this component using logInfo (web has no
				// dedicated warn-level helper, unlike mobile's logger.warn) rather than a warn severity.
				logInfo("[Upload] ActionUploadPhoto: checklist-item upload rejected — invalid file", {
					event: "upload.checklist_document_rejected",
					fileName,
					projectChecklistItemId: this.projectChecklistItemId
				});
			},
			/**
			 * LSP-17928 break-it round 4 (Bugbot, 2nd pass): given the SAME validation pass's own
			 * detectedMimeType (never a second, independently fallible re-read — see the round-4 fix note
			 * at the call site), returns a corrected copy carrying that MIME type when it disagrees with
			 * the file's own declared `type` — so a downstream declared-type-based classifier
			 * (FileUploadDialog's getFilesByType/inferFileCategory) agrees with what gate 1 already
			 * confirmed the content really is. No-ops when detection was inconclusive (`detectedMimeType`
			 * null) or already agrees. Remove once checklist items support documents.
			 */
			// LSP-19132: delegates to the SINGLE shared rule in helpers/fileUploads. This method used to
			// carry its own copy of the logic, which drifted from useFileUploadFlow's — it wrote the detected
			// value verbatim (so `image/jpeg; charset=UTF-8` became the S3 key extension
			// `jpeg; charset=UTF-8`), trusted malformed values, and had no per-part length bound. Four review
			// rounds each found a defect in one copy that the other did not have. Do not re-implement it here.
			correctedFileForUpload(file, detectedMimeType) {
				const outcome = correctDeclaredMimeType(file, detectedMimeType);
				if (outcome.action !== "unchanged") {
					// This correction exists because a real misroute incident (LSP-17928 break-it rounds 4/5)
					// proved a silent mismatch is dangerous — log when it actually fires so a future "why did
					// this upload as a document" triage doesn't have to guess whether the correction ran.
					logInfo(`[Upload] ActionUploadPhoto: ${outcome.action} declared MIME type`, {
						fileName: file.name,
						declaredType: outcome.declaredType,
						detectedMimeType,
						appliedType: outcome.appliedType,
						projectChecklistItemId: this.projectChecklistItemId
					});
				}
				return outcome.file;
			},
			resetFilePicker() {
				this.files = [];
			},
			/**
			 * LSP-19418 break-it round 2 — one validation finished; clear the flag only when it was the
			 * LAST one still running.
			 *
			 * `validating` gates `startButtonDisabled()`, so it decides whether the confirm button will
			 * commit. Clearing it per-run opened that gate while another selection was still being read:
			 * pick a large cloud-backed video, pick a small photo while it validates, and the photo's run
			 * cleared the flag, "Upload N files" became live under-reporting the batch, `save()`
			 * snapshotted without the video, and reset() on success then dropped it — a file the user
			 * picked, uploaded nowhere, with no toast. Counting instead makes the flag mean what the
			 * "Preparing your files…" row already claims: SOMETHING is still being read.
			 *
			 * Break-it round 3 — a run may only settle the count it was itself counted in. An un-stage
			 * zeroes the count along with the epoch (it abandons every run in flight), so a run from
			 * before that point has no share left to give back, and letting it decrement anyway stole
			 * from a genuinely-running validation: abandon a video mid-read, clear, stage a fresh batch,
			 * start a second video, and the abandoned run's late arrival cleared the spinner and opened
			 * the commit gate on a batch still missing the second video — the same loss again, one
			 * indirection further out. The epoch check is what "already zeroed" looks like from here.
			 *
			 * @param {number} unstageToken - the un-stage epoch this run was counted in
			 */
			settleValidation(unstageToken) {
				if (unstageToken !== this.unstageEpoch) {
					return;
				}
				if (this.validationsInFlight > 0) {
					this.validationsInFlight--;
				}
				if (this.validationsInFlight === 0) {
					this.validating = false;
				}
			},
			titleChanged() {
				if (!this.title || this.title == "") {
					this.title = this.computedTitle;
				}
			},
			setCustomer(customer) {
				this.selectedCustomer = customer;
				if (customer) {
					this.validCustomer = true;
					this.name = customer.firstname + " " + customer.lastname;
					this.autoSetName = true;
					this.autoSetNameValue = this.name;

					if (customer.EmailAddresses?.length > 0) {
						this.email = customer.EmailAddresses[0].emailAddress;
						this.emailDisabled = true;
					} else {
						this.emailDisabled = false;
					}

					if (!this.phone && customer.PhoneNumbers?.length > 0) {
						this.phone = customer.PhoneNumbers[0].phoneNumber;
					}
				} else {
					this.validCustomer = false;
					this.emailDisabled = false;
					this.name = "";
					this.email = "";
				}
			},
			async save() {
				// break-it rounds 4-5: ONE identity snapshot for the whole upload, taken before ANY await.
				// save() awaits the large-file confirmation, getMe and updateProject before it ever builds
				// the create request, and the form stays live throughout — so every downstream step (the
				// request, the contact back-fill, the hand-off to FileUploadDialog and the return route)
				// must describe the upload the user actually started, not whatever the form holds by the
				// time the network settles.
				// Shallow COPY, not the live reference. The hazard actually observed in this code is
				// REPLACEMENT — the picker emits a different project object into `newSelectedProject`
				// while save() is awaiting — and capturing the reference already covers that. The copy is
				// defence-in-depth for the narrower case of the captured object being edited in place
				// afterwards, which no current call path does (ProjectAutoComplete re-emits the record
				// unchanged); round 6's probe injects it deliberately. Keeping the copy is close to free
				// and makes the snapshot's identity genuinely immutable rather than conditionally so.
				// break-it round 6
				const uploadProject = this.newSelectedProject ? { ...this.newSelectedProject } : undefined;
				const requestProjectId = uploadProject?.id;
				// ls-heroku-ci (HIGH, verified): the comment above already claims this snapshot covers "the
				// contact back-fill", and it did not -- updateProject read this.selectedCustomer live, and
				// CustomerAutoComplete stays interactive through the confirm-dialog, getMe and updateProject
				// awaits, so changing the contact after clicking upload wrote the WRONG contact into the
				// project's Customers list. That is a persistent write, not a display glitch. Reference, not a
				// copy: the hazard is the picker emitting a different customer object, and unlike uploadProject
				// (whose .Customers array updateProject edits in place) nothing mutates this one.
				const uploadContact = this.selectedCustomer;
				// Captured with the contact they belong to: the create request reads all three together.
				const uploadPhone = (this.phone || "").trim();
				const uploadEmail = (this.email || "").trim();
				const uploadScribePackSelection = this.scribePackSelection;
				// LSP-20406: captured with the other send inputs so a selection edit during the awaited
				// create cannot change which tags the request carries.
				const uploadSessionTags = [...this.selectedSessionTags];
				const projectChecklistItemIdForUpload = this.projectChecklistItemId;
				const filesToUploadCopy = [...this.filesToUpload];
				try {
					// filesChanged() already validates every file via validateUploadFile and getFilesByType
					// before pushing to this.filesToUpload, so no re-validation needed here.

					// Warn about large files before api.createConversation runs — a cancel
					// here must not leave an orphan conversation behind.
					const platform = detectPlatform();
					if (platform !== "desktop") {
						const largeFiles = findLargeFiles(filesToUploadCopy, getLargeFileWarningBytes(platform));
						if (largeFiles.length > 0) {
							// Review follow-up (finding 2): an ABSENT dialog is not a user decline. The optional
							// chain resolved `confirmed` to undefined when $fileUploadDialog or the method was
							// missing, which fell into the `!confirmed` cancel branch below — a silent stop with
							// no toast and no log, the very thing the catch beside it exists to prevent. The
							// sibling openDialog guard already treats a missing dialog as a toastable state.
							const confirmLargeFileWarning = this.$root.$fileUploadDialog?.confirmLargeFileWarning;
							if (typeof confirmLargeFileWarning !== "function") {
								const unavailableMessage = "We couldn't start that upload. Please refresh and try again.";
								this.toast.error(unavailableMessage, { timeout: calculateTimeout(unavailableMessage) });
								console.error(
									"ActionUploadPhoto.save: confirmLargeFileWarning is unavailable, cannot warn about large files",
									{
										tags: { method: "save", file: "ActionUploadPhoto", phase: "confirmLargeFileWarning" },
										extra: {
											platform,
											largeFileCount: largeFiles.length,
											hasUploadDialog: !!this.$root.$fileUploadDialog
										}
									}
								);
								this.starting = false;
								return;
							}
							let confirmed;
							try {
								confirmed = await confirmLargeFileWarning.call(this.$root.$fileUploadDialog, largeFiles, platform);
							} catch (error) {
								// break-it round 5: name the STAGE. Falling through to save()'s generic catch
								// reported "something went wrong" for a failure that is specifically the
								// large-file confirmation dialog, and left the caller unsure whether an
								// upload had started. Nothing has been created yet, so just stop.
								//
								// Review follow-up: stopping SILENTLY was a regression this change introduced —
								// before it, the throw reached save()'s outer catch and the user got a toast.
								// Every sibling stage below (missing openDialog, createConversation reject, bad
								// conversation id) surfaces one, so this must too, or a failed warning on mobile
								// leaves a dead spinner and no explanation.
								const confirmMessage = getMessageFromError(error);
								this.toast.error(confirmMessage, { timeout: calculateTimeout(confirmMessage) });
								console.error("ActionUploadPhoto.save: confirmLargeFileWarning failed, not starting the upload", {
									tags: { method: "save", file: "ActionUploadPhoto", phase: "confirmLargeFileWarning" },
									extra: { platform, largeFileCount: largeFiles.length, error }
								});
								this.starting = false;
								return;
							}
							if (!confirmed) {
								// The user declined. Deliberately silent — they just chose this, and nothing was
								// created. Distinct from the unavailable/rejected cases handled above.
								this.starting = false;
								return;
							}
						}
					}

					const me = await api.getMe();
					// The contact back-fill must target the SAME project the conversation is filed under.
					await this.updateProject(uploadProject, uploadContact);

					const request = {
						forcedUserId: me.user.id,
						name: this.title ?? "Files Upload",
						type: constants.CONVERSATION_TYPE.UPLOADS,
						initiationType: constants.INITIATION_TYPE.ADMIN,
						projectId: requestProjectId,
						scribePackId: uploadScribePackSelection?.pack?.id ?? undefined,
						scribePackName: uploadScribePackSelection?.pack?.name ?? undefined,
						scribePackTaskIds: uploadScribePackSelection?.taskIds ?? undefined,
						scribePackUserInputData: uploadScribePackSelection?.userInputData ?? undefined,
						// LSP-20406: attach the picked tags atomically at creation (server upsertRelatedLabels).
						Labels: uploadSessionTags.length ? uploadSessionTags : undefined
					};

					// Cursor (Medium): the SAME snapshot the project back-fill uses. Snapshotting one and
					// reading the other live is worse than reading both live -- it writes one customer onto
					// the project and a different one onto the conversation for a single upload. phone/email
					// come from the same form and the same awaits, so they are captured with it.
					if (uploadContact?.id) {
						request.visitors = [
							{
								customerId: uploadContact.id,
								phone: uploadPhone,
								email: uploadEmail,
								firstname: uploadContact.firstname,
								lastname: uploadContact.lastname,
								notificationSMS: false,
								notificationEmail: false
							}
						];
					}

					// break-it round 2: the project the conversation is actually filed under. `redirect` used
					// to read the live prop, so a project switched while createConversation was in flight
					// sent the user to a project that does not hold the upload they just started.
					//
					// ls-heroku-ci (LOW): `?? this.projectId` was the same invention this PR removed from the
					// three sibling components — when the request carries NO project, falling back to the prop
					// returns the user to a project the new conversation is not in. `?? ""` instead: the empty
					// string survives the `??` at the return site (undefined would let it re-invent the prop
					// there), and returnToProjectAfterUploadStarted already refuses an empty id and logs the
					// route context, so the refusal is diagnosable rather than silent.
					const projectIdForReturn = requestProjectId ?? "";

					// break-it round 6: check the hand-off BEFORE creating anything. openDialog is what actually
					// uploads; if it is missing we would otherwise leave an empty conversation behind on the
					// project with no files and no way for the user to tell why.
					const uploadDialog = this.$root.$fileUploadDialog;
					if (typeof uploadDialog?.openDialog !== "function") {
						const unavailableMessage = "We couldn't start that upload. Please refresh and try again.";
						this.toast.error(unavailableMessage, { timeout: calculateTimeout(unavailableMessage) });
						console.error(
							"ActionUploadPhoto.save: the upload hand-off (openDialog) is unavailable, not creating a conversation",
							{
								tags: { method: "save", file: "ActionUploadPhoto", phase: "openDialog" },
								extra: { hasUploadDialog: !!uploadDialog, projectId: requestProjectId, fileCount: filesToUploadCopy.length }
							}
						);
						this.starting = false;
						return;
					}

					let resp;
					try {
						resp = await api.createConversation(request);
					} catch (error) {
						// Name the STAGE: a generic save failure cannot tell the reader whether a conversation
						// was created (and so whether a retry duplicates it). break-it round 6
						const createMessage = getMessageFromError(error);
						this.toast.error(createMessage, { timeout: calculateTimeout(createMessage) });
						console.error("ActionUploadPhoto.save: api.createConversation rejected, no conversation was created", {
							tags: { method: "save", file: "ActionUploadPhoto", phase: "createConversation" },
							extra: { projectId: requestProjectId, fileCount: filesToUploadCopy.length, error }
						});
						this.starting = false;
						return;
					}
					const conversation = resp?.conversation;

					// break-it round 4: without an id there is nothing to upload INTO — openDialog would
					// reserve upload targets against `undefined` and the user's files would land nowhere
					// while the UI reported a normal upload. Refuse loudly instead; the caller's catch
					// already clears the button, and no conversation is handed off or navigated to.
					const conversationId = typeof conversation?.id === "string" ? conversation.id.trim() : "";
					if (!conversationId) {
						const malformedMessage = "We couldn't start that upload. Please try again.";
						this.toast.error(malformedMessage, { timeout: calculateTimeout(malformedMessage) });
						console.error(
							"ActionUploadPhoto.save: createConversation response carried no usable conversation id, refusing to upload",
							{
								tags: { method: "save", file: "ActionUploadPhoto" },
								extra: {
									rawConversationId: conversation?.id,
									projectId: projectIdForReturn,
									fileCount: filesToUploadCopy.length
								}
							}
						);
						this.starting = false;
						return;
					}


					const onFileUploaded = (fileType) => {
						const counts = {
							conversationId,
							projectId: projectIdForReturn,
							images: fileType === "image" ? 1 : 0,
							documents: fileType === "document" ? 1 : 0,
							videos: fileType === "video" ? 1 : 0
						};
						// This closure outlives the component — FileUploadDialog calls it per uploaded file,
						// long after we navigated away and unmounted. EventBus dispatches listeners
						// synchronously, so a remounted project page's throwing handler would surface inside
						// FileUploadDialog and mark a file that uploaded FINE as failed. break-it round 6
						try {
							EventBus.$emit("FileUploaded", counts);
						} catch (error) {
							console.warn("ActionUploadPhoto: a FileUploaded progress consumer threw; the upload itself is unaffected", {
								tags: { method: "onFileUploaded", file: "ActionUploadPhoto" },
								extra: { fileType, error }
							});
						}
					};

					uploadDialog.openDialog(
						filesToUploadCopy,
						conversationId,
						conversation.name,
						onFileUploaded,
						projectChecklistItemIdForUpload,
						projectIdForReturn
					);

					// Reset and redirect after starting the upload
					this.reset();
					this.starting = false;
					// Relay the NORMALIZED id: the project page registers this conversation from the
					// close-menu relay, and must not key it differently from the upload. break-it round 5
					this.redirect({ ...conversation, id: conversationId }, projectIdForReturn);
				} catch (e) {
					const message = getMessageFromError(e);
					this.toast.error(message, {
						timeout: calculateTimeout(message)
					});

					console.error(e, {
						tags: {
							method: "save",
							file: "ActionUploadPhoto"
						},
						extra: {
							type: constants.CONVERSATION_TYPE.UPLOADS
						}
					});
				}
			},
			// `projectOverride` lets save() pin this to the project it snapshotted, so a project swapped
			// while an earlier await was in flight cannot get the contact written to it instead. Callers
			// with no snapshot keep the previous live-state behavior. break-it round 5
			async updateProject(projectOverride, contactOverride) {
				// `arguments.length`, not `??`: save() legitimately passes an UNDEFINED snapshot for a
				// project-less upload, and falling back to live state there back-filled the contact onto a
				// project the user picked only AFTER the upload had begun. break-it round 6
				//
				// The contact snapshot gets the same treatment for the same reason (ls-heroku-ci HIGH): an
				// upload with no contact selected passes an UNDEFINED snapshot, and `??` would fall back to
				// whatever the user picked while the upload was already running.
				const targetProject = arguments.length > 0 ? projectOverride : this.newSelectedProject;
				const selectedContact = arguments.length > 1 ? contactOverride : this.selectedCustomer;
				if (targetProject?.id && selectedContact?.id) {
					let contactExistance = false;
					if (!targetProject.Customers) {
						targetProject.Customers = [];
					}
					for (const contact of targetProject.Customers) {
						if (contact.id === selectedContact.id) {
							contactExistance = true;
							break;
						}
					}
					if (!contactExistance) {
						await api.updateProject(targetProject.id, {
							Customers: [...targetProject.Customers, selectedContact]
						});
					}
				}
			},

			redirect(conversation, projectIdForReturn) {
				// break-it round 4: a parent's close-menu listener is someone else's code, and it runs
				// BEFORE the navigation below. Letting it throw here aborted the whole hand-off — the
				// upload was already in flight, so the user was left exactly where the original bug left
				// them, on a page that shows nothing.
				try {
					this.$emit("close-menu", conversation.id);
				} catch (error) {
					console.warn("ActionUploadPhoto.redirect: a close-menu consumer failed, continuing the hand-off", {
						tags: { method: "redirect", file: "ActionUploadPhoto" },
						extra: { conversationId: conversation.id, error }
					});
				}
				if (this.isProjectUploadFlow()) {
					this.returnToProjectAfterUploadStarted(projectIdForReturn, conversation?.id);
					return;
				}
				this.navigateAfterUploadStarted(
					{
						name: "conversation",
						params: {
							id: conversation.id
						},
						query: { from: "homepage" }
					},
					"ActionUploadPhoto.redirect: navigation to the new conversation failed",
					{ conversationId: conversation.id }
				);
			},
			// DELIBERATE DUPLICATION — do not merge these five methods into helpers/projectFlowReturn.ts
			// without reading this first (raised in review; recorded so the split reads as a decision).
			//
			// projectFlowReturn.ts is the shared implementation for the SIBLING flows (ActionPhoto,
			// ActionStartRecording, ActionVideo). This component keeps its own copy because it is the one
			// six break-it rounds hardened, and converging it is not a like-for-like move:
			//   1. The shared helper resolves the project page as name -> route.path -> parsed pathname.
			//      This copy has no route.path step. That difference is not cosmetic — it is exactly what
			//      regressed ActionVideo's existing suite when the helper was first wired up — so adopting
			//      it here silently changes project detection on the hardened path.
			//   2. This copy carries behaviour the siblings have no use for: the upload-identity snapshot,
			//      the `uploadStarted` handoff query, per-branch failure messages, and the conversation
			//      branch's navigation.
			//   3. ActionUploadPhotoProjectReturn.spec.ts drives these as component METHODS — ~24 tests
			//      call redirect(), three of them via `methods.redirect.call(context, …)` with stubbed
			//      context members (the rest through a mounted wrapper.vm). Module imports can be
			//      stubbed by neither route, so converging means rewriting the call sites and, for the
			//      three context-stubbed ones, precisely the assertions those rounds produced.
			// Converging is worth doing, but as its own change with its own adversarial pass — not as a
			// tidy-up riding along with a review fix.
			//
			// break-it round 1: the project-context test used to be `window.location.href.includes("/projects")`,
			// which reads the WHOLE url — so a conversation page carrying `?returnTo=/projects/x`, or an
			// unrelated `/projects-archive` route, was misread as "the user is already on the project page"
			// and the post-upload handoff was silently skipped. Match the PATH only, anchored.
			currentPathname() {
				try {
					// break-it round 3: a CONSTANT base, not window.location.origin — an opaque document
					// ("null" origin) or a missing origin made `new URL` throw for a perfectly ordinary
					// relative href like "/projects/proj-1", which then read as "not a project page".
					// The base is only there to resolve a relative href; its host is never used.
					return new URL(window.location.href, "http://relative.invalid").pathname;
				} catch (error) {
					console.warn("ActionUploadPhoto.currentPathname: could not parse the current URL, assuming not a project page", {
						tags: { method: "currentPathname", file: "ActionUploadPhoto" },
						extra: { href: window.location.href, error }
					});
					return "";
				}
			},
			isOnProjectPage() {
				// The ROUTER is authoritative about which component is mounted; the url string is only a
				// fallback for callers that have no named route. break-it round 2: url-only matching got
				// the answer wrong for an encoded (`/%70rojects/x`), uppercase (vue-router matches
				// case-insensitively), hash-shaped or opaque href, each of which silently skipped the
				// handoff on a page where EntityDetailPage really was mounted.
				const routeName = this.$route?.name;
				if (routeName === "project" || routeName === "project-shared") {
					return true;
				}
				return /^\/projects(\/|$)/i.test(this.currentPathname());
			},
			isProjectUploadFlow() {
				if (this.isOnProjectPage()) {
					return true;
				}
				// vue-router hands a repeated query key over as an array. break-it round 2
				const fromQuery = this.$route?.query?.from;
				return (Array.isArray(fromQuery) ? fromQuery[0] : fromQuery) === "project";
			},
			// LSP-15388: put the user back on the project page once the upload is under way.
			//
			// Desktop opens this component in ActionDialog ON TOP of the project page, so
			// EntityDetailPage stays mounted: it is the only listener for FileUploadDialog's
			// "FileUploaded" event (EntityActions also listens for UploadStarted/UploadFinished, but it
			// renders inside EntityDetailPage's header and unmounts with it), owns the pending-doc
			// skeleton rows and the /upload-status poll that renders the in-flight banner, and is
			// what finally calls reloadDocumentsAfterUploadComplete. Nothing to navigate there.
			//
			// Mobile does NOT open a dialog — ProjectActionFAB.navigateToAction routes to the
			// standalone /upload?projectId=…&from=project page, which unmounts EntityDetailPage.
			// This branch used to emit EventBus "ReloadPhotos", an event with zero $on registrations
			// anywhere in the monorepo, and then deliberately skip the router push. The result was
			// the reported bug: the user was left on a reset, empty upload form with no progress
			// surface, and because the project page was unmounted for the whole upload it never
			// picked up the counts or ran its post-upload reload — so the photos "do not immediately
			// show up in the project" even after navigating back by hand.
			returnToProjectAfterUploadStarted(projectIdForReturn, conversationIdForReturn) {
				// Already on /projects/… — the dialog is layered over a mounted EntityDetailPage.
				if (this.isOnProjectPage()) {
					logInfo("[Upload] ActionUploadPhoto: project page already mounted, no navigation needed", {
						projectId: this.projectId
					});
					return;
				}
				// break-it round 1: `projectId` reaches this component from `route.query.projectId`, which
				// vue-router hands over as a string ARRAY when the key repeats, and as a blank string on a
				// hand-typed or restored url. (The FAB can no longer produce a blank one — the guard added
				// alongside this in ProjectActionFAB.navigateToAction refuses to route without a usable id —
				// so direct navigation is now the live source.) Pushing either as a route param navigates to
				// a project that cannot exist, which is a worse dead end than staying put with a diagnostic.
				// NB: this fallback stays. It is the METHOD's contract for its own callers, several of which
				// pass nothing at all; the capture in save() now passes "" for a project-less create, and ""
				// is not nullish, so it reaches the refusal below rather than being re-invented here.
				const candidateProjectId = projectIdForReturn ?? this.projectId;
				const projectId = typeof candidateProjectId === "string" ? candidateProjectId.trim() : "";
				if (!projectId) {
					console.warn(
						"ActionUploadPhoto.returnToProjectAfterUploadStarted: from=project but no usable projectId, cannot return to the project",
						{
							tags: { method: "returnToProjectAfterUploadStarted", file: "ActionUploadPhoto" },
							extra: { rawProjectId: candidateProjectId, routeQuery: this.$route?.query, href: window.location.href }
						}
					);
					return;
				}
				logInfo("[Upload] ActionUploadPhoto: returning to project so it can track the upload", { projectId });
				// Review follow-up (upload-handoff race): carry the upload identity THROUGH the navigation.
				// `openDialog` emits "UploadStarted" synchronously, before this push is even called, and
				// `$emit("close-menu")` has no parent on the standalone /upload route (so ProjectActionFAB
				// never relays "conversation-created"). EntityDetailPage then registers its EventBus
				// listeners only AFTER `await initializeComponent(...)`, so a small file can upload and
				// finish inside that window with every event missed. A remounted page that READS the
				// handoff from the url cannot miss it; one that has to have HEARD an event can.
				//
				// Only added when there is an id to carry, so a caller with no conversation (and the
				// direct-call tests that pin the plain project target) still pushes a bare route.
				const handoffConversationId = typeof conversationIdForReturn === "string" ? conversationIdForReturn.trim() : "";
				const projectTarget = { name: "project", params: { id: projectId } };
				this.navigateAfterUploadStarted(
					handoffConversationId ? { ...projectTarget, query: { uploadStarted: handoffConversationId } } : projectTarget,
					"ActionUploadPhoto.returnToProjectAfterUploadStarted: navigation to the project failed",
					// Captured, not read from `this` inside the handler: a late rejection would otherwise be
					// logged against whatever projectId the component holds by then. break-it round 1
					{ projectId }
				);
			},
			// The upload is ALREADY under way by the time we navigate, so a navigation problem must never
			// surface as an upload failure. `$router.push` can reject asynchronously (a guard aborting) and
			// can also throw synchronously (an unknown route name) — both stay non-fatal and diagnosed.
			navigateAfterUploadStarted(target, failureMessage, context) {
				const warnNavigationFailed = (error) => {
					console.warn(failureMessage, {
						tags: { method: "navigateAfterUploadStarted", file: "ActionUploadPhoto" },
						extra: { ...context, error }
					});
				};
				try {
					const navigation = Promise.resolve(this.$router.push(target));
					// Both handlers are attached DIRECTLY to the push result, never to a derived promise:
					// vue-router does not reject an aborted, cancelled or duplicated navigation, it RESOLVES
					// with a NavigationFailure (an Error subclass), so a .catch alone never saw the most
					// likely real failure. Chaining .then().catch() instead would leave the ORIGINAL
					// rejection unconsumed. The .then rejection handler is a no-op because .catch already
					// reported it. break-it round 4
					navigation.catch(warnNavigationFailed);
					navigation.then(
						(outcome) => {
							if (outcome instanceof Error) {
								warnNavigationFailed(outcome);
							}
						},
						() => undefined
					);
				} catch (error) {
					warnNavigationFailed(error);
				}
			}
		}
	};
</script>

<style lang="scss" scoped>
	/* LSP-18505: VBtn sizes its own text off --v-btn-size, so the Clear action rendered smaller than
	   the "N files selected" label sitting right next to it. Match the label's text-body-2 (0.875rem)
	   and drop the button letter-spacing so the two read as one line. A utility class can't do this —
	   it loses to .v-btn's own font-size rule. */
	.staged-action-btn {
		font-size: 0.875rem;
		letter-spacing: normal;
	}
	.start-conversation-button {
		font-family: "Inter Tight", sans-serif;
		font-size: 20px;
		color: white;
		margin-left: 10px;
	}
	.reset-span {
		position: relative;
		left: calc(100% - 50px);
		display: inline-block;
		top: -30px;
		height: 0;
	}

	.conversation-card {
		border-radius: 12px;
		min-height: 400px;
		display: flex;
		flex-direction: column;
		justify-content: start;
	}

	.conversation-card .header {
		display: flex;
		margin-bottom: 28px;
		margin-top: 12px;
	}

	.conversation-card textarea {
		color: #212121;
		font-size: 16px;
	}

	.conversation-card h2,
	.conversation-card i {
		font-size: 24px;
		margin-right: 5px;
	}

	.conversation-card .header {
		color: #000;
	}

	.conversation-card .auto-message {
		color: #b3b3b3;
		font-size: 16px;
	}

	.conversation-card .record-switch {
		margin-left: 5px;
		margin-top: 0px;
	}

	.conversation-card .contact-name.reset {
		padding-top: 0px;
	}

	.conversation-card :deep(.v-file-upload) {
		padding-bottom: 8px;
	}

	.field-border-radius {
		border-radius: 6px;
	}

	.v-slide-group__next,
	.v-slide-group__prev {
		display: none;
	}

	/* Email Invite */

	.team-member-share {
		position: absolute;
		padding-top: 8px;
		margin-left: 2px;
		color: rgb(var(--v-theme-brand));
	}

	.customer-phone-search {
		position: absolute;
		top: 0;
		left: 0;
		visibility: hidden;
	}
</style>
